import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Circle, RefreshCw } from "lucide-react";
import { api, errMsg, money } from "@/lib/api";
import { ADMIN_EXTRA as T } from "@/constants/testIds";

const Step = ({ ok, children }) => (
  <li className="flex items-start gap-2 text-sm">
    {ok ? <CheckCircle2 size={18} className="text-[#4E7B68] shrink-0" /> : <Circle size={18} className="text-stone-300 shrink-0" />}
    <span className={ok ? "" : "text-stone-500"}>{children}</span>
  </li>
);

const Checklist = ({ o }) => (
  <ul data-testid={T.liveStatus} className="space-y-2">
    <Step ok={o.livemode}>{o.livemode ? "Real (live) Stripe payment page" : "Test-mode payment page: no real money moves"}</Step>
    <Step ok={["paid", "refunded"].includes(o.payment_status)}>Payment received{o.amount_paid ? `: ${money(o.amount_paid)}` : ""}</Step>
    <Step ok={o.confirmation_sent}>Receipt email sent to {o.email}</Step>
    <Step ok>Order {o.order_id} saved in Orders</Step>
    <Step ok={o.payment_status === "refunded"}>Refunded</Step>
  </ul>
);

export const AdminLiveCheck = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/live-check").then(({ data }) => setS(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  const start = () => run(async () => { const { data } = await api.post("/admin/live-check/start"); window.location.href = data.checkout_url; });
  const refund = () => run(async () => { await api.post("/admin/live-check/refund"); toast.success("Refunded. It can take 5-10 days to show on your card"); await load(); });
  if (!s) return <p className="mt-6 text-stone-500">Loading…</p>;
  const live = s.stripe_mode === "live";
  const o = s.order;
  return (
    <section data-testid={T.liveCheck} className="mt-6 max-w-2xl space-y-4">
      <p data-testid={T.liveMode} className={`rounded-2xl p-4 text-sm ${live ? "bg-[#E8F0EB] text-[#4E7B68]" : "bg-amber-50 text-amber-900"}`}>
        {live ? "Stripe is in live mode. Real cards are charged." : "Stripe is still in test mode, so this check uses test cards (4242 4242 4242 4242). Finish Stripe's live switch, re-publish, then run it again."}
      </p>
      <div className="bg-white border border-[#F0EBE1] rounded-[1.5rem] p-5 space-y-4">
        <p className="text-sm text-stone-600">Places a private $1 order (never shown in the shop) that you pay with your own card. Then come back here, check every step, and refund it.</p>
        <div className="flex flex-wrap gap-2">
          <button data-testid={T.liveStart} disabled={busy} onClick={start} className="px-5 py-2.5 rounded-full bg-[#E06D44] text-white text-sm font-semibold hover:bg-[#C95B33] disabled:opacity-50 transition-colors">Pay $1 test order</button>
          <button data-testid={T.liveRefresh} disabled={busy} onClick={load} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-[#1C1917] text-sm hover:bg-[#1C1917] hover:text-white transition-colors"><RefreshCw size={14} /> Check status</button>
          {o?.payment_status === "paid" && <button data-testid={T.liveRefund} disabled={busy} onClick={refund} className="px-4 py-2.5 rounded-full border border-stone-300 text-sm hover:bg-stone-100 transition-colors">Refund $1</button>}
        </div>
        {o ? <Checklist o={o} /> : <p className="text-sm text-stone-500">No test order yet.</p>}
      </div>
    </section>
  );
};
